import { useCallback, useMemo } from "react";
import { Helmet } from "react-helmet";

import {
  AgentEligibilityErrorType,
  CountyBlockerErrorType,
  RedZoneErrorType,
} from "@covertree/types";
import { Button } from "@covertree/ui";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import {
  selectNewHomeState,
  selectPolicyErrorState,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";

import { SALES_EMAIL_ADDRESS } from "@covertree/consts";
import { useResetNewPolicy } from "@hooks/newPolicy";
import formatPageTitle from "@utils/formatPageTitle";
import { NewPolicyRoutes } from "@routing/paths";
import { ERROR_MESSAGE } from "@covertree/consts";
import { useNavigate } from "react-router";
import { getFullStateName } from "@utils/getFullStateName";

export function ErrorStep() {
  const policyState = useNewBusinessSelector(selectNewHomeState);
  const policyErrorState = useNewBusinessSelector(selectPolicyErrorState);

  const navigate = useNavigate();
  const resetNewPolicy = useResetNewPolicy();

  const errorData = useMemo(() => {
    if (!policyErrorState) return;
    if ("errorType" in policyErrorState)
      return {
        errorType: policyErrorState.errorType,
        errorMessage: policyErrorState.message,
      };
    return;
  }, [policyErrorState]);

  const onCreateNewQuote = useCallback(() => {
    resetNewPolicy(NewPolicyRoutes.SELECT_PRODUCT);
  }, [resetNewPolicy]);

  const { icon, errorContent, allowToRetry, pageTitle } = useMemo(() => {
    if (
      Object.values(AgentEligibilityErrorType).includes(
        errorData?.errorType as AgentEligibilityErrorType
      )
    ) {
      let errorContent: string;
      const state = policyState ? getFullStateName(policyState) : "this state";

      switch (errorData?.errorType) {
        case AgentEligibilityErrorType.NotEligibleToSellPolicyError:
          errorContent = ERROR_MESSAGE.AGENT_NOT_ELIGIBLE(state);
          break;
        case AgentEligibilityErrorType.NotRequestedInNonJITStateError:
          errorContent =
            ERROR_MESSAGE.APPOINTMENT_NOT_REQUESTED_IN_NON_JIT(state);
          break;
        case AgentEligibilityErrorType.NotAppointedInNonJITStateError:
          errorContent = ERROR_MESSAGE.NOT_APPOINTED_IN_NON_JIT(state);
          break;
        case AgentEligibilityErrorType.RejectedInJITStateError:
          errorContent = ERROR_MESSAGE.REJECTED_IN_JIT(state);
          break;
      }

      return {
        icon: "/images/decline-property/decline-property.webp",
        errorContent,
        allowToRetry: false,
        pageTitle: formatPageTitle("Eligibility checks failed"),
      };
    }

    if (
      [
        ...Object.values(RedZoneErrorType),
        ...Object.values(CountyBlockerErrorType),
      ].includes(
        errorData?.errorType as RedZoneErrorType | CountyBlockerErrorType
      )
    ) {
      let errorContent: string;

      switch (errorData?.errorType) {
        case RedZoneErrorType.ActiveFireError:
          errorContent = ERROR_MESSAGE.ACTIVE_FIRE_DANGER;
          break;
        case RedZoneErrorType.ExcessiveRiskError:
          errorContent = ERROR_MESSAGE.HIGH_RISK_FIRE_DANGER;
          break;
        case CountyBlockerErrorType.BlockedCountyError:
          errorContent = errorData.errorMessage;
          break;
      }

      return {
        icon: "/images/decline-property/decline-property.webp",
        errorContent,
        allowToRetry: false,
        pageTitle: formatPageTitle("Risk Is Too High"),
      };
    }

    return {
      icon: "/images/icons/cross.webp",
      errorContent: (
        <>
          Sorry, something went wrong. <br />
          Please try again or email us at&nbsp;
          {SALES_EMAIL_ADDRESS}.
        </>
      ),
      allowToRetry: true,
      pageTitle: formatPageTitle("Error"),
    };
  }, [errorData]);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Box>
        <Stack direction="column">
          <Box mb={5}>
            <Box
              component="img"
              src={icon}
              alt="Error"
              sx={(theme) => ({
                maxWidth: theme.spacing(15),
                ml: 2,
              })}
            />
            <Typography variant="h3">{errorContent}</Typography>
          </Box>
          {allowToRetry ? (
            <Button
              data-testid="try-again-button"
              onClick={() => navigate(-1)}
              sx={{ mb: 5 }}>
              Try again
            </Button>
          ) : (
            <Button
              data-testid="start-quote-again"
              onClick={onCreateNewQuote}
              sx={{ mb: 5 }}>
              Create New Quote
            </Button>
          )}
        </Stack>

        <Typography
          fontStyle="italic"
          color="action.disabled"
          variant="subtitle1">
          CoverTree Inc. &#40;CoverTree&#41; is acting as the managing general
          agent of one or more non-affiliated underwriters in selling this
          insurance policy.
        </Typography>
      </Box>
    </>
  );
}
